// ============================================================================
// BP-13-02: Admin Audit Logs Hooks
// BrightPath Platform - Admin Frontend
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminSystemApi } from '@/features/admin/api/adminApi';
import type { AuditLogListParams } from '@/types/admin';
import { useToast } from '@/core/hooks/useToast';

// =============================================================================
// Query Keys
// =============================================================================

export const auditKeys = {
  all: ['admin', 'audit'] as const,
  logs: () => [...auditKeys.all, 'logs'] as const,
  logList: (params: AuditLogListParams) => [...auditKeys.logs(), params] as const,
  jobs: () => [...auditKeys.all, 'jobs'] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch audit logs
 */
export function useAuditLogs(params: AuditLogListParams) {
  return useQuery({
    queryKey: auditKeys.logList(params),
    queryFn: () => adminSystemApi.getAuditLogs(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch background jobs status
 */
export function useBackgroundJobs() {
  return useQuery({
    queryKey: auditKeys.jobs(),
    queryFn: () => adminSystemApi.getJobs(),
    staleTime: 15 * 1000, // 15 seconds
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Export audit logs
 */
export function useExportAuditLogs() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (params: AuditLogListParams) => adminSystemApi.exportAuditLogs(params),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: 'Export complete',
        description: 'Audit logs have been exported to CSV.',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Export failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Retry a failed job
 */
export function useRetryJob() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (jobId: string) => adminSystemApi.retryJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditKeys.jobs() });
      toast({
        title: 'Job retried',
        description: 'The job has been queued for retry.',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Retry failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Clear all failed jobs
 */
export function useClearFailedJobs() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: () => adminSystemApi.clearFailedJobs(),
    onSuccess: ({ cleared }) => {
      queryClient.invalidateQueries({ queryKey: auditKeys.jobs() });
      toast({
        title: 'Jobs cleared',
        description: `${cleared} failed jobs have been cleared.`,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Clear failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Clear cache
 */
export function useClearCache() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (cacheType?: string) => adminSystemApi.clearCache(cacheType),
    onSuccess: () => {
      toast({
        title: 'Cache cleared',
        description: 'The cache has been cleared successfully.',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Cache clear failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}


